'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { Brain } from 'lucide-react'
import { iqTestQuestions } from '../../data/Questions'
import { 
  initializeTest, 
  getTestData, 
  updateTestData, 
  saveAnswer, 
  completeTest,
  clearTestData 
} from '../../lib/testStorage'

export default function TestPage() {
  const router = useRouter()
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [testData, setTestData] = useState(null)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(30 * 60) // 30 minutes in seconds
  const [timerActive, setTimerActive] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // True during the brief pause between tapping an answer and the next
  // question appearing. Used to keep the Next button from flashing in.
  const [advancing, setAdvancing] = useState(false)
  const advanceTimerRef = useRef(null)

  useEffect(() => {
    document.body.style.overscrollBehavior = 'none'
    return () => { document.body.style.overscrollBehavior = '' }
  }, [])

  useEffect(() => {
    // Clear any existing test data for fresh start during testing
    clearTestData()
    
    // Initialize or get existing test data - PASS questions array
    let data = getTestData()
    if (!data) {
      data = initializeTest(iqTestQuestions) // Pass questions here
    }
    
    setTestData(data)
    setCurrentQuestionIndex(data.currentQuestionIndex || 0)
    
    // Calculate remaining time if test was started before
    if (data.startTime) {
      const elapsed = Math.floor((new Date().getTime() - data.startTime) / 1000)
      const remaining = Math.max(0, 30 * 60 - elapsed)
      setTimeRemaining(remaining)
      if (remaining === 0) {
        setTimerActive(false)
      }
    }
  }, [])

  const currentQuestion = iqTestQuestions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === iqTestQuestions.length - 1
  const progress = ((currentQuestionIndex + 1) / iqTestQuestions.length) * 100

  // Options that are a single character ("A", "B", ...) label choices drawn
  // inside the question image, so they lay out as one compact row instead of
  // full-width stacked rows. Derived from the data rather than keyed off
  // `hasImage` so it stays correct if a letter-option question ever arrives
  // without an image, or vice versa.
  const isCompactOptions =
    Array.isArray(currentQuestion?.options) &&
    currentQuestion.options.every((o) => typeof o === 'string' && o.trim().length === 1)

  // Timer effect
  useEffect(() => {
    if (!timerActive || timeRemaining <= 0) return

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setTimerActive(false)
          // Auto-submit test when time runs out
          setTimeout(() => autoSubmitTest(), 100)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timerActive, timeRemaining])

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

  /**
   * Persist the completion and return its id.
   *
   * Retries transient failures. Losing this call is expensive: with no
   * completionId the payment page has no score, no expiry and no country,
   * so it can neither price correctly nor create a PaymentIntent. Supabase
   * is the failure mode we actually see (a paused project returns 521 for
   * a while, then serves a cold schema cache for a few more requests), and
   * both recover on their own within seconds.
   *
   * 4xx is not retried — a validation failure will fail identically every
   * time. 429 is not retried either; we'd only burn through the limiter.
   */
  const submitCompletionToDB = async (finalData) => {
    const payload = JSON.stringify({
      iqScore: finalData.iqScore,
      percentile: finalData.percentile,
      score: finalData.score,
      totalQuestions: finalData.totalQuestions,
      answers: finalData.answers,
      startTime: finalData.startTime,
      endTime: finalData.endTime,
    })

    const delays = [0, 600, 1500, 3000] // ~5.1s total

    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt] > 0) await sleep(delays[attempt])

      try {
        const response = await fetch('/api/submit-test', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        })

        if (response.ok) {
          const data = await response.json()
          if (data.completionId) {
            if (attempt > 0) console.log(`submit-test succeeded on attempt ${attempt + 1}`)
            return data.completionId
          }
          return null
        }

        // Client errors and rate limits won't change on retry.
        if (response.status < 500 || response.status === 429) {
          console.error('submit-test returned status', response.status)
          return null
        }

        console.warn(
          `submit-test attempt ${attempt + 1}/${delays.length} failed with ${response.status}`
        )
      } catch (err) {
        console.warn(`submit-test attempt ${attempt + 1}/${delays.length} threw:`, err)
      }
    }

    console.error('submit-test failed after all retries')
    return null
  }
 
  const navigateToPayment = (completionId) => {
    if (completionId) {
      router.push(`/payment?cid=${completionId}`)
    } else {
      // Fallback path — payment page reads localStorage instead
      router.push('/payment')
    }
  }
 
  // Auto-submit test when time runs out
  const autoSubmitTest = async () => {
    if (!testData || isSubmitting) return
    setIsSubmitting(true)
    const finalData = completeTest(testData.answers, iqTestQuestions)
    const completionId = await submitCompletionToDB(finalData)
    navigateToPayment(completionId)
  }

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (timeRemaining <= 300) return 'text-red-500' // Last 5 minutes
    if (timeRemaining <= 600) return 'text-yellow-500' // Last 10 minutes
    return 'text-green-500'
  }

  // Get timer background color
  const getTimerBgColor = () => {
    if (timeRemaining <= 300) return 'bg-red-50 border-red-200' // Last 5 minutes
    if (timeRemaining <= 600) return 'bg-yellow-50 border-yellow-200' // Last 10 minutes
    return 'bg-green-50 border-green-200'
  }

  // Mount-time restore only — every navigation sets the selection itself, in
  // goToQuestion, so that it lands in the same render as the index change.
  // `?? null` rather than `|| null` because option A is index 0, and
  // `0 || null` is null, which made a saved "A" come back as nothing selected.
  useEffect(() => {
    if (!testData || !currentQuestion) return
    setSelectedAnswer(testData.answers[currentQuestion.id] ?? null)
  }, [currentQuestionIndex, currentQuestion, testData])

  // Bring the new question into view. Deliberately instant: a smooth scroll
  // animates while the question is already swapping underneath it, which
  // reads as a lurch rather than a transition.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentQuestionIndex])

  // Cancel any pending auto-advance if the component goes away mid-timer.
  useEffect(() => () => clearTimeout(advanceTimerRef.current), [])

  const cancelPendingAdvance = () => {
    clearTimeout(advanceTimerRef.current)
    setAdvancing(false)
  }

  const goToQuestion = (index) => {
    cancelPendingAdvance()

    // Set the selection for the question we are moving TO in the same batch as
    // the index change. Leaving this to the effect above meant React painted a
    // frame of the new question still carrying the OLD question's selection,
    // and because the options animate (transition-all duration-300) that stale
    // highlight faded out over 300ms rather than never appearing.
    //
    // getTestData() rather than the `testData` state: this also runs from a
    // setTimeout closure, where the state can be a render behind, whereas
    // localStorage was written synchronously by saveAnswer.
    const target = iqTestQuestions[index]
    const stored = getTestData()
    setSelectedAnswer(target ? (stored?.answers?.[target.id] ?? null) : null)

    setCurrentQuestionIndex(index)
    updateTestData({ currentQuestionIndex: index })
  }

  /**
   * Selecting an answer advances to the next question on its own, after a
   * short pause.
   *
   * WHY: the answer options sit below the question (and, on the six
   * visual-spatial questions, below a large image). Requiring a separate tap
   * on a Next button underneath all of that meant scrolling down on nearly
   * every question. Selecting IS the intent to move on, so the tap does both.
   *
   * The pause is what makes this safe to use: the selected state renders and
   * is visible for a beat before the question changes, so a mis-tap is
   * something you see happen rather than something that silently registers.
   * Previous still corrects it.
   *
   * The last question is excluded — auto-submitting the whole test on a
   * single tap is far too easy to do by accident, so it keeps an explicit
   * Finish Test button.
   */
  const ADVANCE_DELAY_MS = 300

  const handleAnswerSelect = (answerIndex) => {
    if (!currentQuestion || isSubmitting || timeRemaining === 0) return

    setSelectedAnswer(answerIndex)
    saveAnswer(currentQuestion.id, answerIndex)
    setTestData((prev) =>
      prev
        ? { ...prev, answers: { ...prev.answers, [currentQuestion.id]: answerIndex } }
        : prev
    )

    if (isLastQuestion) return

    clearTimeout(advanceTimerRef.current)
    setAdvancing(true)
    const nextIndex = currentQuestionIndex + 1
    advanceTimerRef.current = setTimeout(() => goToQuestion(nextIndex), ADVANCE_DELAY_MS)
  }

  /**
   * Finish Test on the last question, and the forward step for anyone who
   * used Previous to go back and review — they've already answered the
   * question they're looking at, so tapping the same option again just to
   * move on would be busywork.
   *
   * The answer itself is saved in handleAnswerSelect, so there is nothing to
   * persist here beyond the submit.
   */
  const handleNext = async () => {
    if (selectedAnswer === null || !currentQuestion || isSubmitting) return
    cancelPendingAdvance()

    if (isLastQuestion) {
      setIsSubmitting(true)
      setTimerActive(false)
      const finalAnswers = {
        ...(testData?.answers || {}),
        [currentQuestion.id]: selectedAnswer,
      }
      const finalData = completeTest(finalAnswers, iqTestQuestions)
      const completionId = await submitCompletionToDB(finalData)
      navigateToPayment(completionId)
    } else {
      goToQuestion(currentQuestionIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1)
    }
  }

  if (!testData || !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Brain className="w-5 h-5 text-white animate-pulse" />
          </div>
          {/* This state is the server-rendered HTML, so it's all crawlers see of /test */}
          <h1 className="text-xl font-bold text-gray-800 mb-1">Take the IQ Test</h1>
          <p className="text-sm text-gray-500 mb-3">50 questions, 30 minutes, instant results</p>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20px 20px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Header with Logo - Hidden on mobile for more space */}
      <div className="hidden md:block relative z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200/70 sticky top-0 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex justify-center">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 bg-clip-text text-transparent">
                Best IQ Test
              </h1>
            </div>
          </div>
        </div>
        {/* Gradient fade effect */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header with progress and timer */}
        <div className="mb-6">
          {/* Question Counter and Progress with Timer */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-700">
              Question <span className="font-bold text-blue-600">{currentQuestionIndex + 1}</span> of <span className="font-bold text-gray-800">{iqTestQuestions.length}</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-sm font-medium text-gray-600">
                {Math.round(progress)}% Complete
              </div>
              {/* Responsive Timer - Compact on Mobile, Normal on Desktop */}
              <div className={`${getTimerBgColor()} border px-3 py-2 md:px-5 md:py-3 rounded-lg md:rounded-2xl flex-shrink-0 shadow-sm md:shadow-lg backdrop-blur-sm`}>
                <div className={`text-base md:text-2xl font-bold ${getTimerColor()}`}>
                  {formatTime(timeRemaining)}
                </div>
              </div>
            </div>
          </div>
          
          <div className="w-full bg-white/80 backdrop-blur-sm rounded-full h-2 sm:h-3 shadow-sm border border-gray-200/50">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 sm:h-3 rounded-full transition-all duration-500 shadow-sm"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          {/* Time warning messages */}
          {timeRemaining <= 300 && timeRemaining > 0 && (
            <div className="text-center bg-red-50 border border-red-200 rounded-xl py-2 px-4 mt-4">
              <div className="text-red-600 font-medium animate-pulse text-sm">
                ⚠️ Only {Math.floor(timeRemaining / 60)} minutes remaining!
              </div>
            </div>
          )}
          
          {timeRemaining === 0 && (
            <div className="text-center bg-red-100 border border-red-300 rounded-xl py-2 px-4 mt-4">
              <div className="text-red-700 font-bold text-sm">
                ⏰ Time's up! Submitting your test...
              </div>
            </div>
          )}
        </div>

        {/* Question Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-xl p-6 sm:p-8 mb-8 border border-white/50 relative overflow-hidden">
          {/* Subtle accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600"></div>
          
          <div className="relative z-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 sm:mb-8 leading-relaxed">
              {currentQuestion.question}
            </h2>
            
            {/* The image is sized against the VIEWPORT, not a fixed pixel
                floor. The previous min-h-[300px] (400/450 on larger screens)
                reserved that height whether the image needed it or not —
                these are object-contain, so on a portrait phone it was
                letterboxing inside a box taller than the image, pushing the
                answers off screen for nothing. max-h lets a wide image stay
                wide while guaranteeing it never eats more than half the
                screen. */}
            {currentQuestion.hasImage && (
              <div className="text-center my-4 sm:my-6">
                <div className="bg-white rounded-2xl shadow-lg p-1 sm:p-3 md:p-6 w-full">
                  <Image 
                    src={currentQuestion.imagePath} 
                    alt={`Pattern recognition question ${currentQuestionIndex + 1}`}
                    width={800}
                    height={600}
                    className="w-full h-auto rounded-lg mx-auto max-h-[45vh] sm:max-h-[50vh] object-contain"
                    priority={currentQuestionIndex < 3} // Prioritize first few images
                    sizes="(max-width: 640px) 95vw, (max-width: 768px) 90vw, (max-width: 1200px) 600px, 700px"
                  />
                </div>
              </div>
            )}

            {/* Every image question's options are just the letters A-E,
                referring to choices drawn inside the image. Stacked full
                width they cost ~70px each — up to 350px of scrolling to
                show five single characters, immediately below the image
                they belong to. Laid out in one row they cost ~70px total.

                Non-letter options (real words and phrases) still need the
                full-width rows: they wrap badly in narrow columns. */}
            {isCompactOptions ? (
              <div
                className="grid gap-2 sm:gap-3"
                style={{ gridTemplateColumns: `repeat(${currentQuestion.options.length}, minmax(0, 1fr))` }}
              >
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    className={`flex items-center justify-center py-4 sm:py-5 rounded-2xl border-2 text-xl sm:text-2xl font-bold transition-all duration-300 ${
                      selectedAnswer === index
                        ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-lg scale-[1.04]'
                        : 'border-gray-200 bg-white/80 text-gray-700 hover:border-blue-300 hover:bg-gray-50 hover:shadow-md'
                    } ${timeRemaining === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={timeRemaining === 0}
                    aria-label={`Option ${option}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    className={`w-full flex items-center p-4 sm:p-5 rounded-2xl border-2 transition-all duration-300 text-left hover:bg-gray-50 group ${
                      selectedAnswer === index 
                        ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-lg scale-[1.02]' 
                        : 'border-gray-200 bg-white/80 text-gray-700 hover:border-blue-300 hover:shadow-md'
                    } ${timeRemaining === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.01] active:scale-[0.99]'}`}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={timeRemaining === 0}
                  >
                    <span className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold mr-3 sm:mr-4 flex-shrink-0 transition-all duration-300 ${
                      selectedAnswer === index 
                        ? 'bg-blue-500 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span className="text-base sm:text-lg leading-relaxed">{option}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Navigation
            Next is not shown for an unanswered question — selecting an answer
            is what moves you on. It appears when the question already has an
            answer, which is the case after using Previous to review: those
            users need a way forward that isn't "tap your existing answer
            again". It stays hidden during the auto-advance pause so it
            doesn't flash in for the 300ms before the question changes.
            The last question always shows it, as Finish Test. */}
        <div className="flex justify-between items-center gap-4">
          <button 
            className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-600 rounded-2xl font-medium hover:bg-white hover:border-gray-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0 || timeRemaining === 0}
          >
            Previous
          </button>
          
          {(isLastQuestion || (selectedAnswer !== null && !advancing)) && (
            <button 
              className="px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
              onClick={handleNext}
              disabled={selectedAnswer === null || timeRemaining === 0 || isSubmitting}
            >
              {isLastQuestion ? (isSubmitting ? 'Submitting...' : 'Finish Test') : 'Next'}
            </button>
          )}
        </div>

        {/* Subtle motivational text */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-gray-500 text-sm">
            {currentQuestionIndex < 10 ? 'Take your time and think carefully' : 
             currentQuestionIndex < 30 ? 'You\'re doing great! Keep focused' : 
             currentQuestionIndex < 45 ? 'Almost there! Stay concentrated' : 
             'Final questions - you\'ve got this!'}
          </p>
        </div>
      </div>
    </div>
  )
}